import type { Edge, Node } from '@xyflow/react';
import type { TFunction } from 'i18next';
import type { MindMapEdge, MindMapNode } from '../../../api/strategy-client/strategy-client.type';

type LayoutNode = {
  id: string;
  type: string;
  label: string;
  level: number;
  meta?: Record<string, any>;
};

type LevelInfo = {
  y: number;
  label: string;
};

/**
 * Calculate hierarchical layout positions for mind map nodes
 *
 * @param {MindMapNode[]} graphNodes - The nodes of the graph
 * @param {MindMapEdge[]} graphEdges - The edges of the graph
 * @param {Set<string>} _collapsedNodes - Set of node IDs that are currently collapsed (reserved for future use)
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {nodes: Node[], edges: Edge[], levels: LevelInfo[]} The nodes, edges, and levels of the graph
 */
function calculateHierarchicalLayout(
  graphNodes: MindMapNode[],
  graphEdges: MindMapEdge[],
  _collapsedNodes: Set<string>,
  t: TFunction,
): { nodes: Node[]; edges: Edge[]; levels: LevelInfo[] } {
  const resultNodes: Node[] = [];
  const resultEdges: Edge[] = [];
  const levelInfo: LevelInfo[] = [];

  if (!graphNodes || graphNodes.length === 0) {
    return { nodes: resultNodes, edges: resultEdges, levels: levelInfo };
  }

  // Organize nodes by their type into predefined levels
  const portfolioNodes: MindMapNode[] = [];
  const goalNodes: MindMapNode[] = [];
  const outcomeNodes: MindMapNode[] = [];
  const initiativeNodes: MindMapNode[] = [];
  const epicNodes: MindMapNode[] = [];

  for (const node of graphNodes) {
    switch (node.type) {
      case 'portfolio':
        portfolioNodes.push(node);
        break;
      case 'goal':
        goalNodes.push(node);
        break;
      case 'outcome':
        outcomeNodes.push(node);
        break;
      case 'initiative':
        initiativeNodes.push(node);
        break;
      case 'epic':
        epicNodes.push(node);
        break;
    }
  }

  const portfolioNode = portfolioNodes[0];
  const hasOutcomes = outcomeNodes.length > 0;
  const hasInitiatives = initiativeNodes.length > 0;

  // Build sets for efficient lookup
  const goalIds = new Set(goalNodes.map((n) => n.id));
  const outcomeIdsSet = new Set(outcomeNodes.map((n) => n.id));

  // Find objectives that don't have a goal as parent
  const orphanedOutcomes = new Set<string>();
  if (hasOutcomes) {
    // First assume all outcomes are orphaned
    for (const outcome of outcomeNodes) {
      orphanedOutcomes.add(outcome.id);
    }

    // Then remove outcomes that have a goal parent
    for (const edge of graphEdges) {
      if (goalIds.has(edge.source) && outcomeIdsSet.has(edge.target)) {
        orphanedOutcomes.delete(edge.target);
      }
    }

    // If there are orphaned outcomes, add a "no goals" placeholder
    if (orphanedOutcomes.size > 0) {
      goalNodes.push({
        id: 'no-goals-placeholder',
        type: 'no-goals' as any,
        label: t('strategy.roadmap.no_goal'),
        meta: {},
      });
    }
  }

  // If there are initiatives but no outcomes, add a "no objectives" placeholder
  if (hasInitiatives && !hasOutcomes) {
    outcomeNodes.push({
      id: 'no-objectives-placeholder',
      type: 'no-objectives' as any,
      label: t('strategy.roadmap.no_outcome'),
      meta: {},
    });
  }

  // Build levels array - each level is an array of nodes
  const levels: LayoutNode[][] = [];

  // Level 0: Portfolio
  if (portfolioNodes.length > 0) {
    levels.push(portfolioNodes.map((n) => ({ id: n.id, type: n.type, label: n.label, level: 0, meta: n.meta })));
  }

  // Level 1: Goals
  if (goalNodes.length > 0) {
    levels.push(goalNodes.map((n) => ({ id: n.id, type: n.type, label: n.label, level: 1, meta: n.meta })));
  }

  // Level 2: Objectives/Outcomes
  if (outcomeNodes.length > 0) {
    levels.push(outcomeNodes.map((n) => ({ id: n.id, type: n.type, label: n.label, level: 2, meta: n.meta })));
  }

  // Level 3: Initiatives
  if (initiativeNodes.length > 0) {
    levels.push(initiativeNodes.map((n) => ({ id: n.id, type: n.type, label: n.label, level: 3, meta: n.meta })));
  }

  // Level 4: Epics
  if (epicNodes.length > 0) {
    levels.push(epicNodes.map((n) => ({ id: n.id, type: n.type, label: n.label, level: 4, meta: n.meta })));
  }

  // Layout constants
  const LEVEL_HEIGHT = 400; // Increased to accommodate vertically stacked initiatives
  const NODE_SPACING = 400;
  const START_Y = 60;
  const INITIATIVE_VERTICAL_SPACING = 180; // Vertical spacing between stacked initiatives

  // Position nodes and create level info
  const nodePositions = new Map<string, { x: number; y: number }>();

  // Precompute goal->outcome groupings to cluster outcomes under their goal
  const goalToOutcomes = new Map<string, string[]>();
  for (const edge of graphEdges) {
    if (goalIds.has(edge.source) && outcomeIdsSet.has(edge.target)) {
      if (!goalToOutcomes.has(edge.source)) {
        goalToOutcomes.set(edge.source, []);
      }
      goalToOutcomes.get(edge.source)!.push(edge.target);
    }
  }

  // Map orphaned outcomes to the no-goals placeholder
  if (orphanedOutcomes.size > 0) {
    goalToOutcomes.set('no-goals-placeholder', Array.from(orphanedOutcomes));
  }

  // Precompute outcome->initiative groupings to cluster initiatives under their outcome
  const initiativeIds = new Set(initiativeNodes.map((n) => n.id));
  const outcomeToInitiatives = new Map<string, string[]>();
  for (const edge of graphEdges) {
    if (outcomeIdsSet.has(edge.source) && initiativeIds.has(edge.target)) {
      if (!outcomeToInitiatives.has(edge.source)) {
        outcomeToInitiatives.set(edge.source, []);
      }
      outcomeToInitiatives.get(edge.source)!.push(edge.target);
    }
  }
  // If there are initiatives and no outcomes, place all initiatives under the placeholder
  if (hasInitiatives && !hasOutcomes) {
    outcomeToInitiatives.set(
      'no-objectives-placeholder',
      initiativeNodes.map((n) => n.id),
    );
  }

  levels.forEach((levelNodes, levelIndex) => {
    const levelWidth = (levelNodes.length - 1) * NODE_SPACING;
    const startX = -levelWidth / 2;
    const y = START_Y + levelIndex * LEVEL_HEIGHT;

    // Determine level label
    let levelLabel = '';
    if (levelNodes.length > 0) {
      const firstNodeType = levelNodes[0].type;
      if (firstNodeType === 'portfolio') {
        levelLabel = '';
      } else if (firstNodeType === 'goal' || firstNodeType === 'no-goals') {
        levelLabel = 'STRATEGIC GOALS';
      } else if (firstNodeType === 'outcome' || firstNodeType === 'no-objectives') {
        levelLabel = 'OBJECTIVES';
      } else if (firstNodeType === 'initiative') {
        levelLabel = 'INITIATIVES';
      } else if (firstNodeType === 'epic') {
        levelLabel = 'EPICS';
      }
    }

    // Add level info (skip portfolio level)
    if (levelIndex > 0 && levelLabel) {
      levelInfo.push({ y: y - 80, label: levelLabel });
    }

    // Default placement function
    const placeUniformly = () => {
      levelNodes.forEach((node, nodeIndex) => {
        const x = startX + nodeIndex * NODE_SPACING;
        nodePositions.set(node.id, { x, y });
        resultNodes.push({
          id: node.id,
          type: 'mindMapNode',
          position: { x, y },
          data: {
            label: node.label,
            nodeType: node.type,
            meta: node.meta || {},
          },
        });
      });
    };

    // Special placement for goals: account for outcome children width to prevent overlap
    const placeGoalsWithChildren = () => {
      const MIN_OUTCOME_SPACING = 300;
      const MIN_GOAL_SPACING = 400;
      const GOAL_GROUP_PADDING = 100; // padding between goal groups

      // Calculate width needed for each goal based on its outcomes
      const goalWidths: { id: string; width: number; node: LayoutNode }[] = levelNodes.map((node) => {
        const outcomeIds = goalToOutcomes.get(node.id) || [];

        // Calculate width for each outcome (initiatives are now stacked vertically, so no width impact)
        let totalOutcomesWidth = 0;
        for (const _outcomeId of outcomeIds) {
          // Each outcome uses minimum spacing since initiatives don't affect horizontal width
          totalOutcomesWidth += MIN_OUTCOME_SPACING;
        }

        // Ensure minimum spacing between goals
        const width = Math.max(MIN_GOAL_SPACING, totalOutcomesWidth + GOAL_GROUP_PADDING);
        return { id: node.id, width, node };
      });

      // Calculate total width and position goals
      const totalWidth = goalWidths.reduce((sum, g) => sum + g.width, 0);
      let currentX = -totalWidth / 2;

      goalWidths.forEach(({ id, width, node }) => {
        // Position goal at the center of its allocated width
        const goalX = currentX + width / 2;
        nodePositions.set(id, { x: goalX, y });
        resultNodes.push({
          id: node.id,
          type: 'mindMapNode',
          position: { x: goalX, y },
          data: {
            label: node.label,
            nodeType: node.type,
            meta: node.meta || {},
          },
        });
        currentX += width;
      });
    };

    // Special placement for outcomes: account for initiative children width to prevent overlap
    const placeOutcomesWithChildren = () => {
      const MIN_OUTCOME_SPACING = 300;

      // Calculate width needed for each outcome based on its initiatives
      const outcomeWidths: { id: string; width: number; node: LayoutNode }[] = levelNodes.map((node) => {
        // Since initiatives are now stacked vertically, outcomes don't need extra width for them
        // Keep consistent minimum spacing between outcomes
        const width = MIN_OUTCOME_SPACING;
        return { id: node.id, width, node };
      });

      // Group outcomes by their parent goal
      const goalGroups = new Map<string, typeof outcomeWidths>();
      for (const outcomeWidth of outcomeWidths) {
        // Find which goal this outcome belongs to
        let parentGoalId: string | null = null;
        for (const [goalId, outcomeIds] of goalToOutcomes.entries()) {
          if (outcomeIds.includes(outcomeWidth.id)) {
            parentGoalId = goalId;
            break;
          }
        }

        if (parentGoalId) {
          if (!goalGroups.has(parentGoalId)) {
            goalGroups.set(parentGoalId, []);
          }
          goalGroups.get(parentGoalId)!.push(outcomeWidth);
        }
      }

      // Position outcomes within each goal group
      for (const [goalId, outcomes] of goalGroups.entries()) {
        const parentPos = nodePositions.get(goalId);
        if (!parentPos) {
          continue;
        }

        // Calculate total width for this goal's outcomes
        const totalWidth = outcomes.reduce((sum, o) => sum + o.width, 0);
        let currentX = parentPos.x - totalWidth / 2;

        outcomes.forEach(({ id, width, node }) => {
          // Position outcome at the center of its allocated width
          const outcomeX = currentX + width / 2;
          nodePositions.set(id, { x: outcomeX, y });
          resultNodes.push({
            id: node.id,
            type: 'mindMapNode',
            position: { x: outcomeX, y },
            data: {
              label: node.label,
              nodeType: node.type,
              meta: node.meta || {},
            },
          });
          currentX += width;
        });
      }
    };

    // For outcomes, use special placement that accounts for initiative children
    if (levelNodes.length > 0 && (levelNodes[0].type === 'outcome' || levelNodes[0].type === 'no-objectives')) {
      placeOutcomesWithChildren();
    } else if (levelNodes.length > 0 && levelNodes[0].type === 'initiative') {
      // For initiatives, stack them vertically under their parent outcome
      const placed = new Set<string>();

      // First, place nodes that belong to an outcome group (stacked vertically)
      for (const [outcomeId, childIds] of outcomeToInitiatives.entries()) {
        const parentPos = nodePositions.get(outcomeId);
        if (!parentPos) {
          continue;
        }

        // Stack initiatives vertically under the outcome, centered horizontally
        let currentY = y; // Start at the level's y position
        childIds.forEach((childId) => {
          const childNode = levelNodes.find((n) => n.id === childId);
          if (!childNode) {
            return;
          }
          // Center horizontally on parent outcome, stack vertically
          const x = parentPos.x;
          nodePositions.set(childId, { x, y: currentY });
          resultNodes.push({
            id: childNode.id,
            type: 'mindMapNode',
            position: { x, y: currentY },
            data: {
              label: childNode.label,
              nodeType: childNode.type,
              meta: childNode.meta || {},
            },
          });
          // Move down for next initiative in stack
          currentY += INITIATIVE_VERTICAL_SPACING;
          placed.add(childId);
        });
      }

      // Place any remaining initiatives uniformly (no outcome mapping)
      const remaining = levelNodes.filter((n) => !placed.has(n.id));
      if (remaining.length > 0) {
        const remWidth = (remaining.length - 1) * NODE_SPACING;
        const remStartX = -remWidth / 2;
        remaining.forEach((node, nodeIndex) => {
          const x = remStartX + nodeIndex * NODE_SPACING;
          nodePositions.set(node.id, { x, y });
          resultNodes.push({
            id: node.id,
            type: 'mindMapNode',
            position: { x, y },
            data: {
              label: node.label,
              nodeType: node.type,
              meta: node.meta || {},
            },
          });
        });
      }
    } else if (levelNodes.length > 0 && (levelNodes[0].type === 'goal' || levelNodes[0].type === 'no-goals')) {
      // Goals need special placement to account for outcome children
      placeGoalsWithChildren();
    } else {
      // Other levels keep uniform placement
      placeUniformly();
    }
  });

  // Create edges
  const hasGoalsPlaceholder = goalNodes.some((n) => n.id === 'no-goals-placeholder');
  const hasObjectivesPlaceholder = outcomeNodes.some((n) => n.id === 'no-objectives-placeholder');

  // Add placeholder edges first
  if (hasGoalsPlaceholder && portfolioNode) {
    // Portfolio to "No Strategic Goals" - use same style as portfolio-goal edges
    resultEdges.push({
      id: `${portfolioNode.id}->no-goals-placeholder`,
      source: portfolioNode.id,
      target: 'no-goals-placeholder',
      type: 'smoothstep',
      style: { stroke: '#5E72E4', strokeWidth: 2.5, strokeDasharray: '8,8' },
    });

    // Connect no-goals placeholder to all orphaned objectives - use same style as goal-outcome edges
    for (const outcomeId of orphanedOutcomes) {
      resultEdges.push({
        id: `no-goals-placeholder->${outcomeId}`,
        source: 'no-goals-placeholder',
        target: outcomeId,
        type: 'smoothstep',
        style: { stroke: '#4CAF50', strokeWidth: 2.5, strokeDasharray: '8,8' },
      });
    }
  }

  if (hasObjectivesPlaceholder) {
    const sourceId = hasGoalsPlaceholder ? 'no-goals-placeholder' : portfolioNode?.id || '';
    if (sourceId) {
      resultEdges.push({
        id: `${sourceId}->no-objectives-placeholder`,
        source: sourceId,
        target: 'no-objectives-placeholder',
        type: 'smoothstep',
        style: { stroke: '#cbd5e0', strokeWidth: 2, strokeDasharray: '8,8' },
      });
    }

    // Connect no-objectives placeholder to all initiatives
    for (const initiative of initiativeNodes) {
      resultEdges.push({
        id: `no-objectives-placeholder->${initiative.id}`,
        source: 'no-objectives-placeholder',
        target: initiative.id,
        type: 'smoothstep',
        style: { stroke: '#cbd5e0', strokeWidth: 2, strokeDasharray: '8,8' },
      });
    }
  }

  // Add backend edges (skip portfolio->initiative edges if we have placeholders)
  for (const edge of graphEdges) {
    // Skip portfolio->initiative edges if we have objective placeholder
    if (
      hasObjectivesPlaceholder &&
      edge.source === portfolioNode?.id &&
      initiativeNodes.some((i) => i.id === edge.target)
    ) {
      continue;
    }

    // Skip portfolio->outcome edges only if the outcome has a goal parent (objectives should connect through goals)
    if (
      edge.source === portfolioNode?.id &&
      outcomeNodes.some((o) => o.id === edge.target) &&
      !orphanedOutcomes.has(edge.target)
    ) {
      continue;
    }

    // Skip initiative->epic edges (epics are not displayed in the mind map)
    if (initiativeNodes.some((i) => i.id === edge.source) && epicNodes.some((e) => e.id === edge.target)) {
      continue;
    }

    // Determine edge color and style based on type
    let edgeColor = '#94a3b8';
    let edgeStyle: any = {
      stroke: edgeColor,
      strokeWidth: 2.5,
    };

    if (edge.type?.includes('portfolio-goal')) {
      edgeColor = '#5E72E4'; // Blue for portfolio to goal
      edgeStyle = {
        stroke: edgeColor,
        strokeWidth: 2.5,
        strokeDasharray: '8,8',
      };
    } else if (edge.type?.includes('goal-outcome')) {
      edgeColor = '#4CAF50'; // Green for goal to outcome
      edgeStyle = {
        stroke: edgeColor,
        strokeWidth: 2.5,
        strokeDasharray: '8,8',
      };
    } else if (edge.type?.includes('outcome-initiative')) {
      edgeColor = '#FF9800'; // Orange for outcome to initiative
      edgeStyle = {
        stroke: edgeColor,
        strokeWidth: 2.5,
        strokeDasharray: '8,8',
      };
    } else if (edge.type?.includes('initiative-epic')) {
      edgeColor = '#f59e0b'; // Amber for initiative to epic
      edgeStyle = {
        stroke: edgeColor,
        strokeWidth: 2,
      };
    }

    resultEdges.push({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      style: edgeStyle,
      animated: false,
    });
  }

  return { nodes: resultNodes, edges: resultEdges, levels: levelInfo };
}

export { calculateHierarchicalLayout };
